import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import PlatformOrbit from './PlatformOrbit';
import ParticleField from './ParticleField';

const ACCENT = '#4F46E5';

// ── Dashboard product shot (monochrome) ───────────────────────────
const MOCK_CAMPAIGNS = [
  { name: 'Summer Sale — App Installs', platform: 'TikTok',   status: 'ACTIVE', spend: '$892', trend: '+12%', up: true },
  { name: 'Lead Gen — Morocco Q2',      platform: 'Meta',     status: 'ACTIVE', spend: '$312', trend: '+7%',  up: true },
  { name: 'B2B Decision Makers',        platform: 'LinkedIn', status: 'PAUSED', spend: '$0',   trend: '—',    up: false },
];

function MiniDashboard() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive(a => (a + 1) % MOCK_CAMPAIGNS.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{
      background: '#16122E',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 24px 80px rgba(34,28,81,0.25)',
      width: '100%', maxWidth: 880, margin: '0 auto',
    }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[0,1,2].map(i => <div key={i} style={{ width: 9, height: 9, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }} />)}
        </div>
        <span className="mono-label" style={{ color: 'rgba(255,255,255,0.35)' }}>Campaign Manager — OneBotAds</span>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: ACCENT, boxShadow: `0 0 8px ${ACCENT}` }} />
      </div>

      <div style={{ display: 'flex', gap: 4, padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto' }}>
        {['TikTok', 'Meta', 'Google', 'LinkedIn', 'Snapchat', 'Taboola'].map((p, i) => (
          <span key={p} style={{
            padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500,
            background: i === 0 ? 'rgba(255,255,255,0.08)' : 'transparent',
            border: i === 0 ? '1px solid rgba(255,255,255,0.15)' : '1px solid transparent',
            color: i === 0 ? '#fff' : 'rgba(255,255,255,0.35)',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}>{p}</span>
        ))}
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {[{ l: 'Total Spend', v: '$1,204' }, { l: 'Impressions', v: '781K' }, { l: 'Avg CTR', v: '3.5%' }, { l: 'Active Rules', v: '3' }].map((s, i) => (
          <div key={i} style={{ flex: 1, padding: '16px 20px', borderRight: i < 3 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
            <div className="mono-label" style={{ color: 'rgba(255,255,255,0.3)', marginBottom: 6, fontSize: 10 }}>{s.l}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', fontFamily: "'Space Grotesk',sans-serif" }}>{s.v}</div>
          </div>
        ))}
      </div>

      <div style={{ padding: '6px 0' }}>
        {MOCK_CAMPAIGNS.map((c, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '12px 20px',
            background: active === i ? 'rgba(255,255,255,0.04)' : 'transparent',
            borderLeft: active === i ? `2px solid ${ACCENT}` : '2px solid transparent',
            transition: 'all 0.4s ease',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: active === i ? '#fff' : 'rgba(255,255,255,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{c.platform}</div>
            </div>
            <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', background: c.status === 'ACTIVE' ? 'rgba(129,140,248,0.12)' : 'rgba(255,255,255,0.06)', color: c.status === 'ACTIVE' ? ACCENT : 'rgba(255,255,255,0.4)', flexShrink: 0 }}>{c.status}</span>
            <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 60 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{c.spend}</div>
              <div style={{ fontSize: 10, color: c.up ? ACCENT : 'rgba(255,255,255,0.3)' }}>{c.trend}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Film grain ────────────────────────────────────────────────────
const GRAIN = "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

// ── Hero ──────────────────────────────────────────────────────────
const Hero = () => {
  return (
    <>
      <style>{`
        @keyframes heroRise { from{transform:translateY(110%)} to{transform:translateY(0)} }
        @keyframes heroFade { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        .ed-line { display:block; overflow:hidden; }
        .ed-line > span { display:block; transform:translateY(110%); animation:heroRise 1s cubic-bezier(.19,1,.22,1) forwards; }
        .ed1 > span { animation-delay:.05s } .ed2 > span { animation-delay:.16s } .ed3 > span { animation-delay:.27s }
        .edf1 { animation:heroFade .8s .55s both } .edf2 { animation:heroFade .8s .7s both } .edf3 { animation:heroFade 1s .9s both }
        .ed-cta-solid:hover { transform:translateY(-2px); box-shadow:0 12px 32px rgba(34,28,81,0.3) !important; }
        .ed-cta-solid { transition:all .25s ease; }
        .ed-demo-link:hover { color:#111 !important; }
        .ed-demo-link { transition:color .2s ease; }
        @media (max-width: 900px) { .hero-mono-corner { display:none !important; } }
      `}</style>

      <section id="home" style={{ position: 'relative', overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column' }}>

        {/* Faint top light + interactive particle dust + grain */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '70%', background: 'radial-gradient(ellipse 80% 50% at 30% 0%, rgba(79,70,229,0.05) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <ParticleField />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: GRAIN, opacity: 0.025, pointerEvents: 'none' }} />

        {/* Corner mono labels */}
        <div className="hero-mono-corner mono-label edf1" style={{ position: 'absolute', top: 104, left: 32, lineHeight: 2.3, pointerEvents: 'none' }}>
          <div style={{ color: ACCENT }}>/ AI CAMPAIGN AGENT</div>
          <div style={{ color: '#A3A3AD' }}>/ 06 AD PLATFORMS</div>
          <div style={{ color: '#A3A3AD' }}>/ MCP NATIVE</div>
        </div>
        <div className="hero-mono-corner mono-label edf1" style={{ position: 'absolute', top: 104, right: 32, textAlign: 'right', lineHeight: 2.3, pointerEvents: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', color: '#A3A3AD' }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: ACCENT, display: 'inline-block' }} />
            SYNC — 5 MIN
          </div>
          <div style={{ color: '#A3A3AD' }}>RULES — 24/7</div>
        </div>

        {/* ── Editorial headline + orbit ── */}
        <div className="hero-grid" style={{ position: 'relative', zIndex: 1, maxWidth: 1280, margin: '0 auto', padding: '150px 32px 0', width: '100%', boxSizing: 'border-box', display: 'grid', gridTemplateColumns: 'minmax(320px, 1.1fr) minmax(300px, 1fr)', gap: 24, alignItems: 'center' }}>
          <style>{`
            @media (max-width: 900px) { .hero-grid { grid-template-columns: 1fr !important; padding-top: 120px !important; } }
          `}</style>

          <div>
            <h1 style={{ fontSize: 'clamp(54px,7.5vw,108px)', fontWeight: 700, lineHeight: 0.98, color: '#111', margin: '0 0 40px', letterSpacing: '-0.045em' }}>
              <span className="ed-line ed1"><span>Clear.</span></span>
              <span className="ed-line ed2"><span>Precise.</span></span>
              <span className="ed-line ed3"><span className="serif-accent" style={{ color: ACCENT }}>Automated.</span></span>
            </h1>

            <div className="edf1" style={{ maxWidth: 400, marginBottom: 36 }}>
              <p style={{ fontSize: 17, color: '#6B7280', lineHeight: 1.65, margin: '0 0 14px' }}>
                Six ad platforms. One AI command center. OneBotAds creates, launches and optimises your campaigns while you sleep.
              </p>
              <div className="mono-label" style={{ color: '#A3A3AD', fontSize: 10, lineHeight: 1.9 }}>
                TIKTOK — META — GOOGLE — LINKEDIN — SNAPCHAT — TABOOLA
              </div>
            </div>

            <div className="edf2" style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
              <Link to="/signup" className="ed-cta-solid" style={{ display: 'inline-flex', alignItems: 'center', gap: 12, padding: '17px 34px', borderRadius: 999, background: '#221C51', color: '#fff', fontWeight: 700, fontSize: 15, textDecoration: 'none', fontFamily: "'Space Grotesk',sans-serif" }}>
                Start free <FaArrowRight style={{ fontSize: 12 }} />
              </Link>
              <Link to="/login" className="ed-demo-link" style={{ color: '#52525B', fontSize: 15, textDecoration: 'none', borderBottom: '1px solid rgba(0,0,0,0.25)', paddingBottom: 2 }}>
                See a demo ↗
              </Link>
            </div>
          </div>

          <div className="edf2" style={{ display: 'flex', justifyContent: 'center' }}>
            <PlatformOrbit />
          </div>
        </div>

        {/* ── Product shot peeking from the fold ── */}
        <div className="edf3" style={{ position: 'relative', zIndex: 1, marginTop: 84, padding: '0 32px', WebkitMaskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)', maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)' }}>
          <MiniDashboard />
        </div>
      </section>
    </>
  );
};

export default Hero;
